import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api/errors'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import type {
  AuditAction,
  AuditEntry,
  AuditModule,
  AuditSeverity,
  BillingCycle,
  InfraPlacement,
  InfraServer,
  MailAccount,
  MailAccountStatus,
  MailProvider,
  PaymentMethod,
  PipelineKind,
  PipelineStage,
  PlacementStatus,
  Region,
  RegionInput,
  ServerDetail,
  ServerPayment,
  ServerPaymentInput,
  ServerRole,
  ServerStatus,
  StageInput,
  StageOutcome,
  Tone,
} from './types'

export const adminKeys = {
  all: ['admin'] as const,
  stages: ['admin', 'stages'] as const,
  audit: ['admin', 'audit'] as const,
  infra: ['admin', 'infra'] as const,
  mail: ['admin', 'mail'] as const,
  regions: ['admin', 'regions'] as const,
}

type Row = Record<string, unknown>

function rowsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  if (data && typeof data === 'object') {
    const obj = data as Row
    for (const key of ['items', 'data', 'results', 'stages', 'logs', 'servers', 'placements', 'vendors', 'domains', 'accounts', 'regions']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return asList(data as Row[] | { items?: Row[] })
}
function str(v: unknown, fallback = '') {
  return v == null ? fallback : String(v)
}
function num(v: unknown) {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
function money(v: unknown) {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(2)
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v).toFixed(2)
  return '0.00'
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  const s = String(v ?? '')
  return (allowed as readonly string[]).includes(s) ? (s as T) : fallback
}

const PIPELINES = ['sales', 'install'] as const
const OUTCOMES = ['open', 'won', 'lost'] as const
const TONES = ['primary', 'info', 'success', 'warning', 'danger', 'neutral'] as const
const AUDIT_MODULES = ['auth', 'crm', 'finance', 'tax', 'hr', 'access', 'licensing', 'infra', 'catalog', 'mail'] as const
const AUDIT_ACTIONS = ['login', 'create', 'update', 'delete', 'approve', 'export', 'permission', 'reveal'] as const
const SEVERITIES = ['info', 'notice', 'critical'] as const
const ROLES = ['production', 'demo', 'staging', 'backup'] as const
const SERVER_STATUSES = ['healthy', 'degraded', 'maintenance', 'down'] as const
const CYCLES = ['monthly', 'yearly'] as const
const PAY_METHODS = ['card', 'transfer'] as const
const PLACEMENT_STATUSES = ['running', 'stopped', 'decommissioned'] as const
const ENVS = ['production', 'demo', 'staging'] as const
const MAIL_PROVIDERS = ['google', 'microsoft', 'imap'] as const
const MAIL_STATUSES = ['connected', 'error', 'paused'] as const

function pipelineOf(v: unknown): PipelineKind {
  const s = String(v ?? '')
  if (s === 'installation' || s === 'kurulum' || s === 'install') return 'install'
  return oneOf(v, PIPELINES, 'sales')
}
function outcomeOf(v: unknown): StageOutcome {
  const s = String(v ?? '')
  if (s === 'won' || s === 'lost') return s
  return oneOf(v, OUTCOMES, 'open')
}

function mapStage(row: Row): PipelineStage {
  return {
    id: str(row.id),
    pipeline: pipelineOf(row.pipeline),
    code: str(row.code),
    name: str(row.name),
    outcome: outcomeOf(row.kind ?? row.outcome),
    order: num(row.position ?? row.order),
    probability: num(row.probability),
    slaDays: row.sla_days == null && row.slaDays == null ? null : num(row.sla_days ?? row.slaDays),
    tone: oneOf(row.tone, TONES, 'primary'),
    openCount: num(row.open_count ?? row.openCount),
    openValue: money(row.open_value ?? row.openValue),
    avgDays: num(row.avg_days ?? row.avgDays),
    requiredFields: Array.isArray(row.required_fields) ? (row.required_fields as unknown[]).map(String) : Array.isArray(row.requiredFields) ? (row.requiredFields as unknown[]).map(String) : [],
  }
}

function mapAudit(row: Row): AuditEntry {
  const actor = (row.actor && typeof row.actor === 'object' ? row.actor : {}) as Row
  return {
    id: str(row.id),
    at: str(row.created_at ?? row.at),
    actor: { name: str(actor.name ?? row.actor_name ?? row.actor), email: str(actor.email ?? row.actor_email) },
    module: oneOf(row.module, AUDIT_MODULES, 'auth') as AuditModule,
    action: oneOf(row.action, AUDIT_ACTIONS, 'update') as AuditAction,
    severity: oneOf(row.severity, SEVERITIES, 'info') as AuditSeverity,
    target: str(row.target ?? row.entity),
    link: row.link == null ? undefined : str(row.link),
    summary: str(row.summary ?? row.message),
    ip: str(row.ip),
    device: str(row.user_agent ?? row.device),
    changes: Array.isArray(row.changes)
      ? (row.changes as Row[]).map((c) => ({ field: str(c.field), before: str(c.before), after: str(c.after) }))
      : [],
  }
}

function mapServer(row: Row, vendors: Map<string, string>): InfraServer {
  return {
    id: str(row.id),
    name: str(row.name),
    hostname: str(row.hostname),
    role: oneOf(row.role, ROLES, 'production') as ServerRole,
    provider: vendors.get(str(row.vendor_id)) ?? str(row.provider),
    location: str(row.location),
    ip: str(row.ip_address ?? row.ip),
    os: str(row.os),
    status: oneOf(row.status, SERVER_STATUSES, 'healthy') as ServerStatus,
    vcpu: num(row.vcpu),
    ramGb: num(row.ram_gb ?? row.ramGb),
    diskGb: num(row.disk_gb ?? row.diskGb),
    cpu: num(row.cpu),
    ram: num(row.ram),
    disk: num(row.disk),
    uptimeDays: num(row.uptime_days),
    lastBackupAt: row.last_backup_at == null ? null : str(row.last_backup_at),
    startedAt: str(row.started_at),
    plan: str(row.capacity_note ?? row.plan),
    billingCycle: oneOf(row.billing_cycle, CYCLES, 'monthly') as BillingCycle,
    monthlyCost: money(row.monthly_cost),
    contractNo: str(row.contract_no),
    nextRenewalAt: str(row.next_renewal_at ?? row.renewal_due),
    paymentMethod: oneOf(row.payment_method, PAY_METHODS, 'transfer') as PaymentMethod,
    notes: str(row.notes ?? row.access),
  }
}

function mapPlacement(row: Row, domains: Map<string, string>): InfraPlacement {
  const domainIds = Array.isArray(row.domain_ids) ? (row.domain_ids as unknown[]).map(String) : []
  const host = domains.get(domainIds[0] ?? '') ?? str(row.hostname ?? row.url)
  return {
    id: str(row.id),
    serverId: row.server_id == null ? null : str(row.server_id) || null,
    hostLabel: str(row.host_label ?? host),
    projectId: str(row.project_id),
    projectName: str(row.project_name),
    customerId: row.account_id == null ? null : str(row.account_id) || null,
    customerName: str(row.customer_name ?? row.account_title),
    url: str(row.url ?? host),
    env: oneOf(row.environment ?? row.env, ENVS, 'production'),
    version: str(row.version),
    status: oneOf(row.status === 'active' ? 'running' : row.status === 'to_decommission' || row.status === 'removed' ? 'decommissioned' : row.status, PLACEMENT_STATUSES, 'running') as PlacementStatus,
    dbSizeGb: num(row.db_size_gb),
    createdAt: str(row.created_at),
  }
}

function mapMail(row: Row): MailAccount {
  const members = Array.isArray(row.members) ? (row.members as unknown[]).map(String) : []
  return {
    id: str(row.id),
    email: str(row.email ?? row.address),
    displayName: str(row.display_name ?? row.name ?? row.email),
    provider: oneOf(row.provider ?? row.provider_kind ?? row.provider_type, MAIL_PROVIDERS, 'imap') as MailProvider,
    purpose: row.purpose === 'personal' || row.purpose === 'system' ? row.purpose : 'shared',
    status: oneOf(row.status, MAIL_STATUSES, 'connected') as MailAccountStatus,
    lastSyncAt: str(row.last_sync_at ?? row.last_synced_at),
    quotaUsedGb: num(row.quota_used_gb),
    quotaGb: num(row.quota_gb),
    members,
    sentToday: num(row.sent_today),
    dailyLimit: num(row.daily_limit),
    signature: row.signature === true || Boolean(row.signature_html),
    error: row.error == null ? undefined : str(row.error),
  }
}

function mapRegion(row: Row): Region {
  const cities = Array.isArray(row.cities) ? (row.cities as unknown[]).map(String) : []
  return {
    id: str(row.id),
    code: str(row.code),
    name: str(row.name),
    cities,
    manager: str(row.manager),
    tone: oneOf(row.tone, TONES, 'primary') as Tone,
    customers: num(row.customers),
    liveCustomers: num(row.live_customers),
    openDeals: num(row.open_deals),
    pipelineValue: money(row.pipeline_value),
    quarterTarget: money(row.quarter_target),
    quarterActual: money(row.quarter_actual),
    active: row.active !== false && row.is_active !== false,
  }
}

async function loadInfra() {
  const [servers, placements, vendors, domains] = await Promise.all([
    api.get<unknown>('/api/v1/infra/servers'),
    api.get<unknown>('/api/v1/infra/placements'),
    api.get<unknown>('/api/v1/infra/vendors'),
    api.get<unknown>('/api/v1/infra/domains'),
  ])
  const vendorNames = new Map(rowsOf(vendors).map((row) => [str(row.id), str(row.name)]))
  const domainHosts = new Map(rowsOf(domains).map((row) => [str(row.id), str(row.hostname)]))
  return {
    servers: rowsOf(servers).map((row) => mapServer(row, vendorNames)),
    placements: rowsOf(placements).map((row) => mapPlacement(row, domainHosts)),
  }
}

const useMut = <A, R = unknown>(fn: (arg: A) => Promise<R>) => {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => void qc.invalidateQueries({ queryKey: adminKeys.all }) })
}

export const useStages = () => useQuery({ queryKey: adminKeys.stages, queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/pipeline-stages')).map(mapStage) })

export function useSaveStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: StageInput }) => {
      const stages = qc.getQueryData<PipelineStage[]>(adminKeys.stages) ?? []
      const stage = stages.find((s) => s.id === id)
      return api.post('/api/v1/pipeline-stages', {
        product_id: '',
        pipeline: stage?.pipeline ?? 'sales',
        code: stage?.code ?? id,
        name: input.name,
        kind: stage?.outcome ?? 'open',
        position: stage?.order ?? 0,
        is_active: true,
        probability: input.probability,
        sla_days: input.slaDays,
        required_fields: input.requiredFields,
      })
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: adminKeys.all }),
  })
}

export function useMoveStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dir }: { id: string; dir: -1 | 1 }) => {
      const stages = qc.getQueryData<PipelineStage[]>(adminKeys.stages) ?? []
      const current = stages.find((s) => s.id === id)
      if (!current) return null
      const list = stages.filter((s) => s.pipeline === current.pipeline && s.outcome === 'open').sort((a, b) => a.order - b.order)
      const index = list.findIndex((s) => s.id === id)
      const other = list[index + dir]
      if (!other) return null
      return { id, otherId: other.id, order: other.order, otherOrder: current.order }
    },
    onSuccess: (swap) => {
      if (!swap) return
      qc.setQueryData<PipelineStage[]>(adminKeys.stages, (prev) =>
        prev?.map((s) => (s.id === swap.id ? { ...s, order: swap.order } : s.id === swap.otherId ? { ...s, order: swap.otherOrder } : s)),
      )
    },
  })
}

export const useResetStages = () => useMut((pipeline: PipelineKind) => api.post('/api/v1/pipeline-stages/copy-defaults', { product_id: '', pipeline }))

export const useAuditLog = () => useQuery({ queryKey: adminKeys.audit, queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/audit-logs', { limit: 100 })).map(mapAudit) })

export const useInfra = () => useQuery({ queryKey: adminKeys.infra, queryFn: loadInfra })
export const useServerDetail = (id: string) =>
  useQuery({
    queryKey: [...adminKeys.infra, 'server', id],
    queryFn: async (): Promise<ServerDetail> => {
      const infra = await loadInfra()
      const server = infra.servers.find((s) => s.id === id) ?? infra.servers[0]
      if (!server) throw new ApiError(404, 'NOT_FOUND', 'Sunucu bulunamadı')
      const [history, pay, usageRows] = await Promise.all([
        api.get<unknown>(`/api/v1/infra/servers/${id}/placements-history`).then(rowsOf),
        api.get<Row>(`/api/v1/infra/servers/${id}/payments`),
        api.get<unknown>(`/api/v1/infra/servers/${id}/usage`).then(rowsOf),
      ])
      const placements = history.length ? history.map((row) => mapPlacement(row, new Map())) : infra.placements.filter((p) => p.serverId === id)
      const payments: ServerPayment[] = rowsOf(pay.payments).map((row) => ({
        id: str(row.id),
        serverId: id,
        date: str(row.date),
        periodStart: str(row.period_start),
        periodEnd: str(row.period_end),
        amount: money(row.amount),
        invoiceNo: str(row.invoice_no),
        method: oneOf(row.method, PAY_METHODS, 'transfer') as PaymentMethod,
        status: (row.status === 'paid' || row.status === 'overdue' ? row.status : 'pending') as ServerPayment['status'],
        note: str(row.note),
      }))
      const totals = (pay.totals ?? {}) as Row
      return {
        server,
        payments,
        placements,
        usage: usageRows.map((u) => ({ date: str(u.date), cpu: num(u.cpu), ram: num(u.ram), disk: num(u.disk) })),
        totals: { paid: money(totals.paid), pending: money(totals.pending), thisYear: money(totals.this_year), months: num(totals.months) },
      }
    },
    enabled: !!id,
  })
function serverBody(input: Partial<InfraServer>) {
  const body: Record<string, unknown> = {}
  const set = (key: string, v: unknown) => {
    if (v !== undefined) body[key] = v
  }
  set('name', input.name)
  set('hostname', input.hostname)
  set('role', input.role)
  set('location', input.location)
  set('ip_address', input.ip)
  set('os', input.os)
  set('status', input.status)
  set('vcpu', input.vcpu)
  set('ram_gb', input.ramGb)
  set('disk_gb', input.diskGb)
  set('capacity_note', input.plan)
  set('billing_cycle', input.billingCycle)
  set('monthly_cost', input.monthlyCost === undefined ? undefined : Number(input.monthlyCost))
  set('contract_no', input.contractNo)
  set('next_renewal_at', input.nextRenewalAt)
  set('payment_method', input.paymentMethod)
  set('started_at', input.startedAt)
  set('notes', input.notes)
  return body
}
export const useUpdateServer = () => useMut(({ id, input }: { id: string; input: Partial<InfraServer> }) => api.patch(`/api/v1/infra/servers/${id}`, serverBody(input)))
export const useSaveServerPayment = () =>
  useMut(({ serverId, id, input }: { serverId: string; id?: string; input: ServerPaymentInput }) => {
    const body = {
      date: input.date,
      period_start: input.periodStart,
      period_end: input.periodEnd,
      amount: input.amount,
      invoice_no: input.invoiceNo,
      method: input.method,
      status: input.status === 'paid' ? 'paid' : 'pending',
      note: input.note,
    }
    return id ? api.put(`/api/v1/infra/servers/${serverId}/payments/${id}`, body) : api.post(`/api/v1/infra/servers/${serverId}/payments`, body)
  })
export const useMarkServerPaymentPaid = () => useMut((id: string) => api.post(`/api/v1/infra/server-payments/${id}/paid`, {}))
export const useDeleteServerPayment = () => useMut((id: string) => api.del(`/api/v1/infra/server-payments/${id}`))
export const useSetMaintenance = () => useMut(({ id, on }: { id: string; on: boolean }) => api.post(`/api/v1/infra/servers/${id}/maintenance`, { on }))
export const useTogglePlacement = () => useMut((id: string) => api.post(`/api/v1/infra/placements/${id}/toggle`, {}))
export const useDecommission = () => useMut((id: string) => api.post(`/api/v1/infra/placements/${id}/decommission`, {}))

export const useCreateVendor = () => useMut((body: { name: string; vendorType: string; contactEmail: string }) => api.post('/api/v1/infra/vendors', { name: body.name, vendor_type: body.vendorType, contact_email: body.contactEmail }))
export const useUpdateVendor = () => useMut(({ id, notes }: { id: string; notes: string }) => api.patch(`/api/v1/infra/vendors/${id}`, { notes }))
export const useCreateServer = () => useMut((body: { name: string; role: string; capacityNote: string; access: string }) => api.post('/api/v1/infra/servers', { name: body.name, role: body.role, capacity_note: body.capacityNote, access: body.access }))
export const useCreateDomain = () =>
  useMut((body: { hostname: string; status: string; vendorId: string; renewalDue: string; paymentStatus: string }) =>
    api.post('/api/v1/infra/domains', { hostname: body.hostname, status: body.status, vendor_id: body.vendorId, renewal_due: body.renewalDue, payment_status: body.paymentStatus }),
  )
export const useAssignDomain = () =>
  useMut(({ id, serverId, accountId, free }: { id: string; serverId?: string; accountId?: string; free?: boolean }) =>
    api.patch(`/api/v1/infra/domains/${id}/assign`, free ? { free: true } : { server_id: serverId, account_id: accountId }),
  )
export const useCreatePlacement = () =>
  useMut((body: { serverId: string; accountId: string; domainIds: string[] }) => api.post('/api/v1/infra/placements', { server_id: body.serverId, account_id: body.accountId, domain_ids: body.domainIds }))
export const useUnlockVault = () => useMut((pin: string) => api.post('/api/v1/vault/unlock', { pin }))
export const useSetVaultPin = () => useMut((pin: string) => api.post('/api/v1/vault/pin', { pin }))

export const useMailAccounts = () =>
  useQuery({
    queryKey: adminKeys.mail,
    queryFn: async () => {
      const [accounts, providers] = await Promise.all([api.get<unknown>('/api/v1/mail/accounts'), api.get<unknown>('/api/v1/mail/providers')])
      const providerOf = new Map(rowsOf(providers).map((row) => [str(row.id), str(row.type ?? row.provider ?? row.name)]))
      return rowsOf(accounts).map((row) => mapMail({ ...row, provider: row.provider ?? providerOf.get(str(row.provider_id)) }))
    },
  })
export const useResyncMail = () => useMut((id: string) => api.post(`/api/v1/mail/accounts/${id}/resync`, {}))
export const useToggleMailPause = () => useMut((id: string) => api.post(`/api/v1/mail/accounts/${id}/toggle-pause`, {}))

export const useRegions = () => useQuery({ queryKey: adminKeys.regions, queryFn: async () => rowsOf(await api.get<unknown>('/api/v1/regions')).map(mapRegion) })
export const useSaveRegion = () =>
  useMut(({ id, input }: { id?: string; input: RegionInput }) => (id ? api.put(`/api/v1/regions/${id}`, { name: input.name, code: input.code }) : api.post('/api/v1/regions', { code: input.code, name: input.name })))
